
import Vue, { VueConstructor } from "vue"
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import dateFormatMixin from '@/mixins/DateFormatMixin.vue'

interface Timer extends HTMLFormElement {
  selectingHour: boolean
}

interface DateTimeType {
  dateObject: Date | string,
  dateString: string,
  timeString: string
}

const DEFAULT_DATE = ''
const DEFAULT_TIME = '00:00:00'
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd'
const DEFAULT_TIME_FORMAT = 'HH:mm:ss'
const DEFAULT_DIALOG_WIDTH = 340

export default (Vue as VueConstructor<Vue & InstanceType<typeof dateFormatMixin>>).extend({
  name: 'DateTimePicker',
  model: {
    prop: 'datetime',
    event: 'input'
  },
  mixins: [
    dateFormatMixin
  ],
  props: {
    datetime: {
      default: {
        dateObject: "",
        dateString: "",
        timeString: ""
      } as DateTimeType
    },
    disabled: {
      type: Boolean
    },
    loading: {
      type: Boolean
    },
    label: {
      type: String,
      default: ''
    },
    dialogWidth: {
      type: Number,
      default: DEFAULT_DIALOG_WIDTH
    },
    dateFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT
    },
    timeFormat: {
      type: String,
      default: 'HH:mm'
    },
    textFieldProps: {
      type: Object
    },
    datePickerProps: {
      type: Object
    },
    timePickerProps: {
      type: Object
    },
    required: {
      type: Boolean,
      default: false
    },
    classStyle: {
      type: String,
      default: "mr-2"
    },
    width: {
      type: String,
      default: "200"
    },
    showTime: {
      type: Boolean,
      default: true
    },

  },
  data() {
    return {
      display: false,
      activeTab: 0,
      date: DEFAULT_DATE,
      time: DEFAULT_TIME,
      dateFormatted: null as null | string,
      manualInputValue: '' as string,
      validationError: '' as string,
    }
  },
  mounted() {
    this.init()
    // Initialiser manualInputValue avec la date formatée
    this.manualInputValue = this.formattedDatetime
    this.$emit('input', {
      dateObject: this.selectedDatetime,
      dateString: this.date,
      timeString: this.time
    })

  },
  computed: {
    dateTimeFormat(): string {
      return this.dateFormat + ' ' + this.timeFormat
    },
    defaultDateTimeFormat(): string {
      return DEFAULT_DATE_FORMAT + ' ' + DEFAULT_TIME_FORMAT
    },
    formattedDatetime(): string {
      if (!this.selectedDatetime) return ''
      
      // Formater la date en français
      const dateObj = this.selectedDatetime
      const day = String(dateObj.getDate()).padStart(2, '0')
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const year = dateObj.getFullYear()
      
      // Afficher les heures seulement si showTime est true
      if (this.showTime) {
        const hours = String(dateObj.getHours()).padStart(2, '0')
        const minutes = String(dateObj.getMinutes()).padStart(2, '0')
        let timeString = `${hours}:${minutes}`
        
        // Ajouter les secondes seulement si elles sont présentes dans this.time
        if (this.time && this.time.includes(':') && this.time.split(':').length >= 3) {
          const seconds = String(dateObj.getSeconds()).padStart(2, '0')
          timeString += `:${seconds}`
        }
        
        return `${day}/${month}/${year} ${timeString}`
      } else {
        return `${day}/${month}/${year}`
      }
    },
    inputValue(): string {
      return this.manualInputValue || this.formattedDatetime
    },
    selectedDatetime(): Date | null {
      if (this.date) {
        // Utiliser une heure par défaut (00:00) seulement si showTime est false ou si aucune heure n'est définie
        let timeToUse = this.time
        if (!this.showTime || !timeToUse) {
          timeToUse = '00:00'
        }
        let datetimeString = this.date + ' ' + timeToUse
        
        // S'assurer que le temps a le bon format (HH:mm:ss)
        if (timeToUse.length === 5) {
          datetimeString += ':00'
        } else if (timeToUse.length === 2) {
          datetimeString += ':00:00'
        } else if (timeToUse.length === 4) {
          datetimeString = this.date + ' ' + timeToUse.substring(0, 2) + ':' + timeToUse.substring(2, 4) + ':00'
        }
        return parse(datetimeString, this.defaultDateTimeFormat, new Date())
      } else {
        return null
      }
    },
    dateSelected(): boolean {
      return !this.date
    },
    requiredRule(): boolean | ((value: string) => boolean | string) {
      if (this.required) {
        return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.required_field')
      }
      return true
    },
    validationRule(): boolean | ((value: string) => boolean | string) {
      return (v: string) => {
        if (!v) return true // Pas de validation si vide
        if (this.validationError) {
          return this.validationError
        }
        return true
      }
    },
  },
  methods: {
    init() {
      if (!this.datetime.dateObject) {
        return
      }

      let initDateTime
      if (this.datetime.dateObject instanceof Date) {
        initDateTime = this.datetime.dateObject
        this.date = format(initDateTime, DEFAULT_DATE_FORMAT)
        // Définir l'heure seulement si showTime est true et qu'une heure existe
        if (this.showTime) {
          if (this.datetime.timeString && this.datetime.timeString !== '00:00') {
            this.time = format(initDateTime, 'HH:mm')
          }
          // Ne pas définir d'heure par défaut si aucune heure n'est fournie
        } else {
          this.time = '00:00'
        }
      } else if (typeof this.datetime.dateObject === 'string' || String instanceof this.datetime.dateObject) {
        try {
          initDateTime = parse(this.datetime.dateObject, this.dateTimeFormat, new Date())
          this.date = format(initDateTime, DEFAULT_DATE_FORMAT)
          // Définir l'heure seulement si showTime est true et qu'une heure existe
          if (this.showTime) {
            if (this.datetime.timeString && this.datetime.timeString !== '00:00') {
              this.time = format(initDateTime, 'HH:mm')
            }
            // Ne pas définir d'heure par défaut si aucune heure n'est fournie
          } else {
            this.time = '00:00'
          }
        } catch (e) {
          console.warn('Error during date initialization:', e)
        }
      }
    },
    openDialog() {
      this.display = true
    },
    okHandler() {
      this.resetPicker()
      // Update input with formatted date
      this.manualInputValue = this.formattedDatetime
      this.$emit('input', {
        dateObject: this.selectedDatetime,
        dateString: this.date,
        timeString: this.time,
        updated: true
      })
    },
    clearHandler() {
      this.resetPicker()
      // Restore original value in input
      this.manualInputValue = this.formattedDatetime
      this.init()
    },
    resetPicker() {
      this.display = false
      this.activeTab = 0
      if (this.$refs.timer) {
        (this.$refs.timer as Timer).selectingHour = true
      }
    },
    showTimePicker() {
      this.activeTab = 1
    },
    onFieldClick(event: Event) {
      // Allow manual input
      event.stopPropagation()
    },
    onFieldFocus(event: Event) {
      // Allow manual input
      event.stopPropagation()
    },
    onFieldKeydown(event: KeyboardEvent) {
      // Si showTime est false, limiter à 10 caractères maximum
      if (!this.showTime && this.manualInputValue.length >= 10) {
        // Permettre les touches de navigation et de suppression
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 
          'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Enter'
        ]
        if (!allowedKeys.includes(event.key)) {
          event.preventDefault()
          return
        }
      }
      
      // Allow manual input, open modal only with Enter key
      if (event.key === 'Enter') {
        this.display = true
      }
    }
  },
  watch: {
    datetime: function () {
      this.init()
      // Update manualInputValue when datetime changes
      this.manualInputValue = this.formattedDatetime
    },
    manualInputValue: function (newValue: string, oldValue: string) {
      // Si showTime est false, limiter strictement à 10 caractères maximum
      if (!this.showTime && newValue.length > 10) {
        this.$nextTick(() => {
          this.manualInputValue = newValue.substring(0, 8)
        })
        return
      }
      
      // Nettoyer l'entrée pour ne garder que les chiffres
      let numbers = newValue.replace(/\D/g, '')
      
      // Si showTime est false, limiter à 8 chiffres (DDMMYYYY) seulement
      if (!this.showTime) {
        numbers = numbers.substring(0, 8)
      } else {
        // Limiter à 14 chiffres (DDMMYYYYHHMMSS) si showTime est true
        numbers = numbers.substring(0, 14)
      }

      // Formatage progressif façon IBAN - sans ajouter automatiquement les heures et secondes
      let formatted = ''
      if (numbers.length > 0) {
        formatted += numbers.substring(0, 2)
      }
      if (numbers.length >= 3) {
        formatted += '/' + numbers.substring(2, 4)
      } else if (numbers.length > 2) {
        formatted += '/' + numbers.substring(2)
      }
      if (numbers.length >= 5) {
        formatted += '/' + numbers.substring(4, 8)
      } else if (numbers.length > 4) {
        formatted += '/' + numbers.substring(4)
      }
      // N'ajouter l'espace et les heures que si showTime est true et que l'utilisateur les tape
      if (this.showTime) {
        if (numbers.length >= 9) {
          formatted += ' ' + numbers.substring(8, 10)
        } else if (numbers.length > 8) {
          formatted += ' ' + numbers.substring(8)
        }
        if (numbers.length >= 11) {
          formatted += ':' + numbers.substring(10, 12)
        } else if (numbers.length > 10) {
          formatted += ':' + numbers.substring(10)
        }
        // N'ajouter les secondes que si l'utilisateur les tape
        if (numbers.length >= 14) {
          formatted += ':' + numbers.substring(12, 14)
        } else if (numbers.length > 12) {
          formatted += ':' + numbers.substring(12)
        }
      }

      // Si le formaté diffère de la saisie, on le remplace (évite la boucle infinie)
      if (formatted !== newValue) {
        this.$nextTick(() => {
          this.manualInputValue = formatted
        })
      }

      // Validation et parsing - ne traiter que si on a au moins une date complète (8 chiffres)
      if (numbers.length >= 8) {
        const day = parseInt(numbers.substring(0, 2))
        const month = parseInt(numbers.substring(2, 4))
        const year = parseInt(numbers.substring(4, 8))
        
        // Vérifier d'abord les bornes de la date
        if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
          this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
          return
        }

        // Si showTime est true, valider les heures/minutes/secondes
        if (this.showTime) {
          // Si on a des heures, les valider
          if (numbers.length >= 10) {
            const hours = parseInt(numbers.substring(8, 10))
            if (hours < 0 || hours > 23) {
              this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
              return
            }
          }

          // Si on a des minutes, les valider
          if (numbers.length >= 12) {
            const minutes = parseInt(numbers.substring(10, 12))
            if (minutes < 0 || minutes > 59) {
              this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
              return
            }
          }

          // Si on a des secondes, les valider
          if (numbers.length >= 14) {
            const seconds = parseInt(numbers.substring(12, 14))
            if (seconds < 0 || seconds > 59) {
              this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
              return
            }
          }
        }

        try {
          // Créer la date avec ou sans heures/minutes/secondes selon showTime et ce qui a été saisi
          let hours = 0
          let minutes = 0
          let seconds = 0
          
          if (this.showTime) {
            if (numbers.length >= 10) {
              hours = parseInt(numbers.substring(8, 10))
            }
            if (numbers.length >= 12) {
              minutes = parseInt(numbers.substring(10, 12))
            }
            if (numbers.length >= 14) {
              seconds = parseInt(numbers.substring(12, 14))
            }
          }

          const parsedDate = new Date(year, month - 1, day, hours, minutes, seconds)
          
          // Vérifier que la date existe vraiment (ex: 32/01/2023)
          if (parsedDate.getDate() !== day || parsedDate.getMonth() !== month - 1 || parsedDate.getFullYear() !== year) {
            this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
            return
          }

          // Validation temporelle seulement si showTime est true et qu'on a une date complète avec heures
          if (this.showTime && numbers.length >= 10) {
            const now = new Date()
            const currentYear = now.getFullYear()
            const currentMonth = now.getMonth()
            const currentDay = now.getDate()
            const maxFutureDate = new Date(currentYear, currentMonth + 12, currentDay)
            if (parsedDate > maxFutureDate) {
              this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
              return
            }
            const minPastDate = new Date(currentYear - 100, currentMonth, currentDay)
            if (parsedDate < minPastDate) {
              this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
              return
            }
          }

          // Si tout est bon
          this.validationError = ''
          this.date = format(parsedDate, DEFAULT_DATE_FORMAT)
          
          // Mettre à jour l'heure seulement si showTime est true et qu'elle a été saisie
          if (this.showTime) {
            if (numbers.length >= 10) {
              let timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
              // Ajouter les secondes seulement si elles ont été saisies
              if (numbers.length >= 14) {
                timeString += `:${String(seconds).padStart(2, '0')}`
              }
              this.time = timeString
            }
            // Ne pas ajouter d'heure par défaut si aucune heure n'a été saisie
          } else {
            // Si showTime est false, toujours utiliser 00:00
            this.time = '00:00'
          }

          this.$emit('input', {
            dateObject: parsedDate,
            dateString: this.date,
            timeString: this.time,
            updated: true
          })
        } catch (e) {
          this.validationError = this.$vuetify.lang.t('$vuetify.invalid_date') + (' (' + (this.showTime ? 'jj/mm/aaaa hh:mm' : 'jj/mm/aaaa') + ')')
          console.warn('Error parsing date:', e)
        }
      } else {
        this.validationError = ''
      }
    }
  }
})
